<template>
    <div class="container-fluid">
        <div class="row">
            <div class="col-lg-8 col-xlg-9 col-md-7">
                <div class="card">
                    <div class="card-header">
                        <h4 class="card-title">Packaging</h4>
                        <h6 class="card-subtitle">Allocation of commodities to fulfill sale order</h6>
                    </div>

                    <form @submit.prevent="handleSubmit" class="form-horizontal" v-if="['INVOICED', 'BACK_ORDER'].includes(so.status)">
                        <div class="card-body card-blue">
                            <div class="form-group row">
                                <label class="col-sm-3 text-right control-label col-form-label" for="commodity">Commodity</label>
                                <div class="col-sm-7">
                                    <div :class="{'is-invalid': submitted && errors.has('commodity')}" class="form-control p-0-0">
                                        <multiselect
                                                :clear-on-select="false"
                                                :close-on-select="true"
                                                :options="so.items"
                                                :preserve-search="true"
                                                :searchable="false"
                                                :selectLabel="''"
                                                :show-labels="false"
                                                @select="clearWarehouse"
                                                data-vv-as="commodity"
                                                id="commodity"
                                                label="commodity"
                                                name="commodity"
                                                placeholder="Select Commodity"
                                                track-by="name"
                                                v-model="commodity"
                                                v-validate="'required'">
                                            <template slot="singleLabel" slot-scope="{ option }">
                                                {{ option.name }}
                                            </template>
                                            <template slot="option" slot-scope="props">
                                                <div class="option__desc">
                                                    <h5>{{ props.option.name }}</h5>
                                                    <h6 v-if="props.option">Pending {{props.option.quantity.amount + props.option.fulfilled}}{{props.option.quantity.unitSymbol}}</h6>
                                                </div>
                                            </template>
                                        </multiselect>
                                    </div>
                                    <div class="invalid-feedback" v-if="submitted && errors.has('commodity')">{{ errors.first('commodity') }}</div>
                                </div>
                            </div>
                            <div class="form-group row">
                                <label class="col-sm-3 text-right control-label col-form-label" for="warehouse">Warehouse</label>
                                <div class="col-sm-7">
                                    <div :class="{'is-invalid': submitted && errors.has('warehouse')}" class="form-control p-0-0">
                                        <multiselect
                                                :clear-on-select="false"
                                                :close-on-select="true"
                                                :hide-selected="true"
                                                :internal-search="false"
                                                :limit="3"
                                                :loading="isLoading"
                                                :max-height="600"
                                                :options="warehouses"
                                                :options-limit="300"
                                                :searchable="true"
                                                :selectLabel="''"
                                                :show-no-results="false"
                                                @search-change="getWareHouses"
                                                @input="getConversionRate(stockUnit, commodity.unit_symbol)"
                                                data-vv-as="warehouse"
                                                id="warehouse"
                                                label="warehouse"
                                                name="warehouse"
                                                open-direction="bottom"
                                                placeholder="Search warehouse"
                                                track-by="id"
                                                v-model="warehouse"
                                                v-validate="'required'">
                                            <template slot="singleLabel" slot-scope="props">
                                                {{props.option.name}}
                                            </template>
                                            <template slot="option" slot-scope="props">
                                                <div class="option__desc">
                                                    <h5>{{ props.option.name }}</h5>
                                                    <h6>{{ props.option.line1 +', '+ props.option.city+', '+ props.option.country}}</h6>
                                                    <h5>{{ commodity.name }} available {{ getAvailable(props.option) | units }}</h5>
                                                </div>
                                            </template>
                                            <span slot="noResult">Warehouse not found</span>
                                        </multiselect>
                                    </div>
                                    <div class="invalid-feedback" v-if="submitted && errors.has('warehouse')">{{ errors.first('warehouse') }}</div>
                                </div>
                            </div>

                            <div class="form-group row" v-if="warehouse">
                                <label class="col-sm-3 text-right control-label col-form-label" for="warehouse">Conversion Rate</label>
                                <div class="col-sm-7">
                                    <input class="form-control"
                                           data-vv-as="conversion_rate"
                                           id="conversion_rate"
                                           name="conversion_rate"
                                           placeholder="conversion_rate"
                                           readonly
                                           v-model="conversionRate">
                                </div>
                            </div>
                            <div class="form-group row" v-if="warehouse">
                                <label class="col-sm-3 text-right control-label col-form-label" for="warehouse">Conversion</label>
                                <div class="col-sm-4">
                                    <div class="input-group mb-3">
                                        <div class="input-group-prepend" v-if="stockUnit">
                                            <span class="input-group-text" id="basic-addon1">{{stockUnit}}</span>
                                        </div>
                                        <input class="form-control"
                                               placeholder="From"
                                               type="text"
                                               v-model="fromVal"
                                               v-on:keyup="fromValChange">
                                    </div>
                                </div>
                                <div class="col-sm-4">
                                    <div class="input-group mb-3">
                                        <div class="input-group-prepend">
                                            <span class="input-group-text" id="basic-addon2">{{commodity.unit_symbol}}</span>
                                        </div>
                                        <input class="form-control"
                                               placeholder="To"
                                               type="text"
                                               v-model="toVal"
                                               v-on:keyup="toValChange">
                                    </div>
                                </div>
                            </div>

                            <div class="form-group row">
                                <label class="col-sm-3 text-right control-label col-form-label" for="quantity">Quantity</label>
                                <div class="col-sm-7">
                                    <input :class="{'is-invalid': submitted && errors.has('quantity')}"
                                           class="form-control"
                                           data-vv-as="quantity"
                                           id="quantity"
                                           name="quantity"
                                           placeholder="Quantity"
                                           ref="quantity"
                                           step="any"
                                           type="number"
                                           v-model="quantity"
                                           v-validate="'required'">
                                    <div class="invalid-feedback" v-if="submitted && errors.has('quantity')">{{ errors.first('quantity') }}</div>
                                </div>
                            </div>

                            <div class="form-group row" v-if="commodity">
                                <label class="col-sm-3 text-right control-label col-form-label" for="units">Unit</label>
                                <div class="col-sm-7">
                                    <input class="form-control"
                                           id="units"
                                           readonly
                                           type="text"
                                           v-model="commodity.unit_name">
                                </div>
                            </div>
                        </div>

                        <div class="card-footer">
                            <div class="form-group m-b-0 text-right">
                                <div class="button-group">
                                    <button class="btn waves-effect waves-light btn-success" type="submit">Save</button>
                                    <button @click="reset()" class="btn waves-effect waves-light btn-secondary" type="button">Reset</button>
                                </div>
                            </div>
                        </div>
                    </form>

                    <div class="card-body card-blue" v-if="['PACKED','SHIPPED','FULFILLED', 'CLOSED'].includes(so.status)">
                        <h5>This order has been packed on {{so.transition.PACKED | long}}</h5>
                    </div>
                </div>

                <div class="card">
                    <div class="card-header">
                        <h4 class="card-title">Packaging Summary</h4>
                    </div>

                    <div class="table-responsive" style="clear: both;">
                        <table class="table table-hover">
                            <thead>
                            <tr class="bg-light">
                                <th class="text-center">#</th>
                                <th>Commodity</th>
                                <th>Quantity</th>
                                <th>Warehouse</th>
                            </tr>
                            </thead>
                            <tbody>
                            <tr v-bind:key="index" v-for="(ff, index) in fulfillmentList">
                                <td class="text-center">{{index + 1}}</td>
                                <td>{{ff.commodity_name}}</td>
                                <td>{{ff.quantity | units}}</td>
                                <td>{{ff.warehouse_name}}</td>
                            </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>

            <div class="col-lg-4 col-xlg-3 col-md-5">
                <div class="card">
                    <div class="card-body">
                        <h4 class="card-title">Commodities pending</h4>
                        <template v-for="item in so.items">
                            <h5 class="m-t-30">{{item.name}}</h5>
                            <span class="text-black-50"
                                  v-if="item.pending">Pending {{item.pending.amount!==undefined&item.pending.amount!==null?item.pending:0 | units}} of {{item.quantity | units}}</span>
                            <div class="progress" v-if="item.pending">
                                <div :style="{ width: (1 - Number(item.pending.amount!==undefined&item.pending.amount!==null?item.pending.amount:0)/Number(item.quantity.amount))*100 + '%' }"
                                     class="progress-bar bg-success wow animated progress-animated"
                                     role="progressbar">
                                    <span class="sr-only">60% Complete</span>
                                </div>
                            </div>
                        </template>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
    import Multiselect from 'vue-multiselect';
    import UnitSelect from "../../common/UnitSelect";
    import Quantity from "../../../../models/Quantity";
    import {mapGetters} from "vuex";
    import _ from 'lodash';

    export default {
        name: "Fulfillment",
        props: ["saleid"],
        computed: {
            ...mapGetters(["currentUser", "currentOrg"])
        },
        components: {Multiselect, UnitSelect},
        data() {
            return {
                conversionRate: null,
                fromVal: 1,
                toVal: null,
                stockUnit: null,
                toUnit: null,
                so: {
                    total: 0,
                    items: []
                },
                submitted: false,
                isLoading: false,
                commodity: null,
                warehouse: null,
                quantity: null,
                warehouses: [],
                fulfillmentList: []
            };
        },
        mounted() {
            this.getSaleOrder();
        },
        methods: {
            getConversionRate(fromUnit, toUnit) {
                this.conversionRate = Quantity.getConversionRate(fromUnit, toUnit);
                this.toVal          = this.conversionRate;
            },
            fromValChange(event) {
                this.toVal = Number(this.conversionRate) * Number(this.fromVal);
            },
            toValChange() {
                this.fromVal = Number(this.toVal) / Number(this.conversionRate);
            },
            async getSaleOrder() {
                let reply = await this.$http.get(`/marketplaces/${this.currentOrg.marketplace_id}/sales/${this.saleid}`);
                this.so   = reply.data;
                await this.getFulfillmentList();

                this.so.items.forEach(item => {
                    let total         = this.fulfillmentList.filter(({commodity_id}) => commodity_id === item.commodity_id).reduce((sum, elem) => sum + Number(elem.quantity.amount), 0);
                    item["fulfilled"] = total;
                    item["pending"]   = {
                        amount: item.quantity.amount + total,
                        unitName: item.unit_name,
                        unitSymbol: item.unit_symbol
                    }
                });

                this.so.items = JSON.parse(JSON.stringify(this.so.items));
            },
            async getWareHouses(searchText) {
                this.warehouses = [];
                if(this.commodity) {
                    let reply = await this.$http.get(`/marketplaces/${this.currentOrg.marketplace_id}/purchases/warehouses/search`, {
                        params: {
                            search: searchText
                        }
                    });

                    // show warehouses having selected commodity
                    reply.data.forEach(wh => {
                        wh.stock.every(st => {
                            if(st.commodity_id === this.commodity.commodity_id) {
                                this.warehouses.push(wh);
                                return false;
                            } else {
                                return true;
                            }
                        })
                    });
                }
            },
            async getFulfillmentList() {
                let reply = await this.$http.get(`/marketplaces/${this.currentOrg.marketplace_id}/sales/${this.saleid}/pack`);

                this.fulfillmentList = reply.data;
            },
            async handleSubmit() {
                this.submitted = true;
                let valid      = await this.$validator.validate();
                if(valid) {
                    let availableCommodity = _.find(this.warehouse.stock, {commodity_id: this.commodity.commodity_id});
                    let orderedCommodity   = _.find(this.so.items, {commodity_id: this.commodity.commodity_id});
                    if(Number(this.quantity.amount) > Number(availableCommodity.available)) {
                        this.$toastr('error', 'Quantity cannot exceed warehouse stock', 'Failed');
                    } else if(Number(this.quantity.amount) > Number(orderedCommodity.pending.amount)) {
                        this.$toastr('error', 'Quantity cannot exceed ordered quantity', 'Failed');
                    } else {
                        let pack = {
                            warehouseId: this.warehouse.id,
                            saleItemId: this.commodity.id,
                            quantity: this.quantity,
                            unit_id: this.commodity.unit_id,
                            conversion_rate: this.conversionRate
                        };

                        try {
                            await this.$http.post(`/marketplaces/${this.currentOrg.marketplace_id}/sales/${this.saleid}/pack`, pack);
                            this.$toastr('success', 'Commodity packed', 'Success');
                            this.reset();
                            this.fromVal = 1;
                            await this.getSaleOrder();
                        } catch(e) {
                            this.$toastr('error', e.response.data.reason, 'Failed');
                        }
                    }
                }
            },
            reset() {
                this.submitted  = false;
                this.commodity  = null;
                this.warehouses = [];
                this.warehouse  = null;
                this.quantity   = null;
            },
            getAvailable(warehouse) {
                let commodity  = _.find(warehouse.stock, {commodity_id: this.commodity.commodity_id});
                this.stockUnit = commodity.unit_symbol;
                return {
                    amount: commodity.available,
                    unitName: commodity.unit_name,
                    unitSymbol: commodity.unit_symbol
                };
            },
            clearWarehouse() {
                this.warehouses = [];
                this.warehouse  = null;
                this.quantity   = null;
            }
        }
    }
</script>

<style scoped>
    .progress {
        height: 10px;
    }

    .progress-bar {
        height: 10px;
    }
</style>
